import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";
import {
  canAccessDramaData,
  getFirstMenuPath,
  hasMenuPathAccess,
  isPathAllowedAfterMenuSync,
} from "@/utils/menuAccess";

const routes: RouteRecordRaw[] = [
  {
    path: "/login",
    name: "Login",
    component: () => import("@/views/Login.vue"),
    meta: { requiresAuth: false },
  },
  {
    path: "/m/login",
    name: "MobileLogin",
    component: () => import("@/views/mobile/MobileLogin.vue"),
    meta: { requiresAuth: false, isStatic: true },
  },
  {
    path: "/m/home",
    name: "MobileHome",
    component: () => import("@/views/mobile/MobileHome.vue"),
    meta: { isStatic: true },
  },
  {
    path: "/m/drama-ranking",
    name: "MobileDramaRanking",
    component: () => import("@/views/data/DramaRanking.vue"),
    meta: { isStatic: true },
  },
  {
    path: "/m/optimizer-ranking",
    name: "MobileOptimizerRanking",
    component: () => import("@/views/mobile/OptimizerRanking.vue"),
    meta: { isStatic: true },
  },
  {
    path: "/m/designer-ranking",
    name: "MobileDesignerRanking",
    component: () => import("@/views/mobile/DesignerRanking.vue"),
    meta: { isStatic: true },
  },
  {
    path: "/m/creative-ranking",
    name: "MobileCreativeRanking",
    component: () => import("@/views/mobile/CreativeRanking.vue"),
    meta: { isStatic: true },
  },
  {
    path: "/m/user-trace-ranking",
    name: "MobileUserTraceRanking",
    component: () => import("@/views/mobile/UserTraceRanking.vue"),
    meta: { isStatic: true },
  },
  {
    path: "/",
    component: () => import("@/layouts/MainLayout.vue"),
    redirect: "/home",
    meta: { isStatic: true },
    children: [
      // 创建广告 (独立页面但保留顶部导航)
      {
        path: "ad/create",
        redirect: (to) => {
          const platform = String(to.query.platform || "").toLowerCase();
          if (["facebook", "tiktok", "google"].includes(platform)) {
            const { platform: _platform, ...rest } = to.query;
            return { path: `ad/create/${platform}`, query: rest };
          }
          return "/ad/create/facebook";
        },
        meta: {
          title: "创建广告",
          topMenu: "promotion",
          hideSidebar: true,
          isStatic: true,
        },
      },
      {
        path: "ad/create/facebook",
        name: "AdCreateFacebook",
        component: () => import("@/views/promotion/AdCreateFacebook.vue"),
        meta: {
          title: "创建广告(Facebook)",
          topMenu: "promotion",
          hideSidebar: true,
          isStatic: true,
        },
      },
      {
        path: "ad/create/tiktok",
        name: "AdCreateTikTok",
        component: () => import("@/views/promotion/AdCreateTikTok/index.vue"),
        meta: { title: "创建广告(TikTok)", topMenu: "promotion", hideSidebar: true, isStatic: true },
      },
      {
        path: "ad/create/google",
        name: "AdCreateGoogle",
        component: () => import("@/views/promotion/AdCreateGoogle.vue"),
        meta: {
          title: "创建广告(Google)",
          topMenu: "promotion",
          hideSidebar: true,
          isStatic: true,
        },
      },
      // 主页
      {
        path: "home",
        name: "Home",
        component: () => import("@/views/Home.vue"),
        meta: { title: "主页", topMenu: "home", isStatic: true },
      },

      // 设计仪表盘（顶部入口由服务端菜单配置控制）
      {
        path: "designer-dashboard",
        name: "DesignerDashboard",
        component: () => import("@/views/designer/DesignerDashboard.vue"),
        meta: {
          title: "设计仪表盘",
          topMenu: "DesignerDashboard",
          hideSidebar: true,
          isStatic: true,
        },
      },

      // 素材管理 (前端硬编码入口) - 已改为动态路由
      {
        path: "creative",
        name: "Creative",
        component: () => import("@/views/creative/Creative.vue"),
        meta: { title: "素材", topMenu: "creative", isStatic: true },
      },
      // 素材标签管理
      {
        path: "creative/tags",
        name: "CreativeTags",
        component: () => import("@/views/creative/Tags.vue"),
        meta: {
          title: "标签管理",
          topMenu: "creative",
          isStatic: true,
          hideSidebar: true,
        },
      },

      // 作品管理 (作品中心) - 已改为动态路由
      {
        path: "dramas",
        name: "Dramas",
        component: () => import("@/views/dramas/Dramas.vue"),
        meta: { title: "作品", topMenu: "dramas", isStatic: true },
      },

      // 数据模块
      {
        path: "data",
        name: "Data",
        component: () => import("@/views/data/DataLayout.vue"),
        meta: { title: "数据", topMenu: "data" },
        redirect: "/data/summary",
        children: [
          {
            path: "summary",
            name: "DataSummary",
            component: () => import("@/views/data/Analytics.vue"),
            meta: { title: "综合", dimension: "summary" },
          },
          {
            path: "date",
            name: "DataDate",
            component: () => import("@/views/data/Analytics.vue"),
            meta: { title: "日期", dimension: "date" },
          },
          {
            path: "daily-trend",
            name: "DataDailyTrend",
            component: () => import("@/views/data/AnalyticsHour.vue"),
            meta: { title: "每日趋势", dimension: "daily-trend" },
          },
          {
            path: "campaign",
            name: "DataCampaign",
            component: () => import("@/views/data/Analytics.vue"),
            meta: { title: "广告系列", dimension: "campaign" },
          },
          {
            path: "optimizer",
            name: "DataOptimizer",
            component: () => import("@/views/data/Analytics.vue"),
            meta: { title: "优化师", dimension: "optimizer" },
          },
          {
            path: "designer",
            name: "DataDesigner",
            component: () => import("@/views/data/Analytics.vue"),
            meta: { title: "设计师", dimension: "designer" },
          },
          {
            path: "drama",
            name: "DataDrama",
            component: () => import("@/views/data/Analytics.vue"),
            meta: { title: "投放作品", dimension: "drama" },
          },
          {
            path: "creative",
            name: "DataCreative",
            component: () => import("@/views/data/Analytics.vue"),
            meta: { title: "素材", dimension: "creative" },
          },
          {
            path: "creative-new",
            name: "DataCreativeNew",
            component: () => import("@/views/data/Analytics.vue"),
            meta: { title: "素材-新", dimension: "creative-new" },
          },
          {
            path: "ltv-first",
            name: "DataLTVFirst",
            component: () => import("@/views/data/Analytics.vue"),
            meta: { title: "LTV（首次）", dimension: "ltv-first" },
          },
          {
            path: "ltv-last",
            name: "DataLTVLast",
            component: () => import("@/views/data/Analytics.vue"),
            meta: { title: "LTV（末次）", dimension: "ltv-last" },
          },
          {
            path: "user-trace",
            name: "DataUserTrace",
            component: () => import("@/views/data/Analytics.vue"),
            meta: { title: "用户回溯", dimension: "user-trace" },
          },
          {
            path: "utc-8-campaign",
            name: "DataUtc8Campaign",
            component: () => import("@/views/data/AnalyticsUtc8Campaign.vue"),
            meta: { title: "UTC-8 广告系列", dimension: "utc-8-campaign" },
          },
          {
            path: "utc-7-campaign",
            name: "DataUtc7Campaign",
            component: () => import("@/views/data/AnalyticsUtc7Campaign.vue"),
            meta: { title: "UTC-7 广告系列", dimension: "utc-7-campaign" },
          },
        ],
      },
      // 推广模块 - Facebook
      {
        path: "promotion/facebook",
        name: "PromotionFacebook",
        component: () => import("@/views/promotion/PromotionLayout.vue"),
        meta: { title: "Facebook", topMenu: "promotion", platform: "facebook" },
        redirect: "/promotion/facebook/campaigns",
        children: [
          {
            path: "accounts",
            name: "PromotionFacebookAccounts",
            component: () => import("@/views/promotion/AdAccounts.vue"),
            meta: { title: "广告账户", tab: "accounts", platform: "facebook" },
          },
          {
            path: "campaigns",
            name: "PromotionFacebookCampaigns",
            component: () => import("@/views/promotion/Campaigns.vue"),
            meta: { title: "广告系列", tab: "campaigns", platform: "facebook" },
          },
          {
            path: "adsets",
            name: "PromotionFacebookAdSets",
            component: () => import("@/views/promotion/AdSets.vue"),
            meta: { title: "广告组", tab: "adsets", platform: "facebook" },
          },
          {
            path: "ads",
            name: "PromotionFacebookAds",
            component: () => import("@/views/promotion/Ads.vue"),
            meta: { title: "广告", tab: "ads", platform: "facebook" },
          },
          {
            path: "creatives",
            name: "PromotionFacebookCreatives",
            redirect: "/promotion/facebook/campaigns",
            meta: { title: "素材", tab: "creatives", platform: "facebook" },
          },
        ],
      },
      // 推广模块 - TikTok
      {
        path: "promotion/tiktok",
        name: "PromotionTikTok",
        component: () => import("@/views/promotion/PromotionLayout.vue"),
        meta: { title: "TikTok", topMenu: "promotion", platform: "tiktok" },
        redirect: "/promotion/tiktok/campaigns",
        children: [
          {
            path: "accounts",
            name: "PromotionTikTokAccounts",
            component: () => import("@/views/promotion/AdAccounts.vue"),
            meta: { title: "广告账户", tab: "accounts", platform: "tiktok" },
          },
          {
            path: "campaigns",
            name: "PromotionTikTokCampaigns",
            component: () => import("@/views/promotion/Campaigns.vue"),
            meta: { title: "广告系列", tab: "campaigns", platform: "tiktok" },
          },
          {
            path: "adsets",
            name: "PromotionTikTokAdSets",
            component: () => import("@/views/promotion/AdSets.vue"),
            meta: { title: "广告组", tab: "adsets", platform: "tiktok" },
          },
          {
            path: "ads",
            name: "PromotionTikTokAds",
            component: () => import("@/views/promotion/Ads.vue"),
            meta: { title: "广告", tab: "ads", platform: "tiktok" },
          },
          {
            path: "creatives",
            name: "PromotionTikTokCreatives",
            redirect: "/promotion/tiktok/campaigns",
            meta: { title: "素材", tab: "creatives", platform: "tiktok" },
          },
        ],
      },
      // 推广模块 - Google
      {
        path: "promotion/google",
        name: "PromotionGoogle",
        component: () => import("@/views/promotion/PromotionLayout.vue"),
        meta: { title: "Google", topMenu: "promotion", platform: "google" },
        redirect: "/promotion/google/campaigns",
        children: [
          {
            path: "accounts",
            name: "PromotionGoogleAccounts",
            component: () => import("@/views/promotion/AdAccounts.vue"),
            meta: { title: "广告账户", tab: "accounts", platform: "google" },
          },
          {
            path: "campaigns",
            name: "PromotionGoogleCampaigns",
            component: () => import("@/views/promotion/Campaigns.vue"),
            meta: { title: "广告系列", tab: "campaigns", platform: "google" },
          },
          {
            path: "adsets",
            name: "PromotionGoogleAdSets",
            component: () => import("@/views/promotion/AdSets.vue"),
            meta: { title: "广告组", tab: "adsets", platform: "google" },
          },
          {
            path: "ads",
            name: "PromotionGoogleAds",
            component: () => import("@/views/promotion/Ads.vue"),
            meta: { title: "广告", tab: "ads", platform: "google" },
          },
          {
            path: "creatives",
            name: "PromotionGoogleCreatives",
            component: () => import("@/views/promotion/Creatives.vue"),
            meta: { title: "素材", tab: "creatives", platform: "google" },
          },
        ],
      },
      // 推广链接（独立于三大平台，在左侧菜单展示）
      {
        path: "promotion/:platform(facebook|tiktok)/automation",
        redirect: "/promotion/automation",
      },
      {
        path: "promotion/automation",
        name: "PromotionAutomation",
        component: () => import("@/views/promotion/AutomationCenter.vue"),
        meta: { title: "自动投放", topMenu: "promotion", isStatic: true },
      },
      {
        path: "promotion/automation-plan-dashboard",
        name: "PromotionAutomationPlanDashboard",
        component: () => import("@/views/promotion/AutomationCenter.vue"),
        meta: { title: "方案数据看板", topMenu: "promotion", isStatic: true },
      },
      {
        path: "promotion/links",
        name: "PromotionGlobalLinks",
        component: () => import("@/views/promotion/PromotionLinks.vue"),
        meta: { title: "推广链接", topMenu: "promotion" },
      },
      {
        path: "promotion/social-accounts",
        name: "PromotionSocialAccounts",
        component: () => import("@/views/promotion/SocialAccounts.vue"),
        meta: { title: "社媒账号", topMenu: "promotion" },
      },
      {
        path: "promotion/distribution-accounts",
        name: "PromotionDistributionAccounts",
        component: () => import("@/views/promotion/DistributionAccounts.vue"),
        meta: { title: "分销账号", topMenu: "promotion" },
      },
      {
        path: "promotion/distribution-works",
        name: "PromotionDistributionWorks",
        component: () => import("@/views/promotion/DistributionWorks.vue"),
        meta: { title: "分销作品", topMenu: "promotion" },
      },
      // 监控配置
      {
        path: "promotion/monitor-rules",
        name: "MonitorRules",
        component: () => import("@/views/promotion/MonitorRules.vue"),
        meta: { title: "监控配置", topMenu: "promotion" },
      },
      // 监控仪表盘
      {
        path: "promotion/monitor-dashboard",
        name: "MonitorDashboard",
        component: () => import("@/views/promotion/MonitorDashboard.vue"),
        meta: { title: "监控仪表盘", topMenu: "promotion" },
      },
      // 回传配置：扣量规则全局配置仅限超级管理员/管理员操作。
      {
        path: "promotion/postback-rules",
        name: "PostbackRules",
        component: () => import("@/views/promotion/PostbackRules.vue"),
        meta: { title: "回传配置", topMenu: "promotion", requireAdmin: true },
      },
      // 管理模块
      {
        path: "manage",
        name: "Manage",
        component: () => import("@/views/manage/ManageLayout.vue"),
        meta: { title: "管理", topMenu: "manage" },
        redirect: "/manage/ad-accounts",
        children: [
          {
            path: "ad-accounts",
            name: "ManageAdAccounts",
            component: () => import("@/views/manage/AdAccountsManage.vue"),
            meta: { title: "广告账户", sideMenu: "ad-accounts" },
          },
          {
            path: "value-rules",
            name: "ManageValueRules",
            component: () => import("@/views/manage/ValueRules.vue"),
            meta: { title: "价值规则集", sideMenu: "value-rules" },
          },
          {
            path: "data-exports",
            name: "ManageDataExports",
            component: () => import("@/views/manage/DataExportManagement.vue"),
            meta: { title: "数据导出管理", sideMenu: "data-exports" },
          },
          {
            path: "apps",
            name: "ManageApps",
            component: () => import("@/views/manage/Apps.vue"),
            meta: { title: "应用", sideMenu: "apps" },
          },
          {
            path: "pages",
            name: "ManagePages",
            component: () => import("@/views/manage/Pages.vue"),
            meta: { title: "主页", sideMenu: "pages" },
          },
          {
            path: "pixels",
            name: "ManagePixels",
            component: () => import("@/views/manage/Pixels.vue"),
            meta: { title: "像素", sideMenu: "pixels" },
          },
          {
            path: "users",
            name: "ManageUsers",
            component: () => import("@/views/manage/Users.vue"),
            meta: { title: "用户管理", sideMenu: "users" },
          },
          {
            path: "roles",
            name: "ManageRoles",
            component: () => import("@/views/manage/Roles.vue"),
            meta: { title: "角色管理", sideMenu: "roles" },
          },
          {
            path: "organization",
            name: "ManageOrganization",
            component: () => import("@/views/manage/Organization.vue"),
            meta: { title: "组织管理", sideMenu: "organization" },
          },
          {
            path: "menus",
            name: "ManageMenus",
            component: () => import("@/views/manage/Menus.vue"),
            meta: { title: "菜单管理", sideMenu: "menus" },
          },
          {
            path: "auth",
            name: "ManageAuth",
            component: () => import("@/views/manage/MediaAuth.vue"),
            meta: { title: "媒体授权", sideMenu: "auth" },
          },
          {
            path: "tasks",
            name: "ManageTasks",
            component: () => import("@/views/manage/Tasks.vue"),
            meta: { title: "任务", sideMenu: "tasks" },
          },
          {
            path: "default-metrics",
            name: "ManageDefaultMetrics",
            component: () => import("@/views/manage/DefaultMetrics.vue"),
            meta: { title: "默认指标", sideMenu: "default-metrics" },
          },
          {
            path: "settings",
            name: "ManageSettings",
            component: () => import("@/views/manage/Settings.vue"),
            meta: { title: "系统设置", sideMenu: "settings" },
          },
          {
            path: "audience-packages",
            name: "ManageAudiencePackages",
            component: () => import("@/views/asset/AudiencePackages.vue"),
            meta: { title: "受众包", sideMenu: "audience-packages" },
          },
        ],
      },
    ],
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

const isMobileDevice = () => {
  if (typeof window === "undefined") return false;

  const ua = navigator.userAgent || "";
  const isIPad =
    /iPad/i.test(ua) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const isAndroidTablet = /Android/i.test(ua) && !/Mobile/i.test(ua);
  const isTablet = isIPad || isAndroidTablet;

  if (isTablet) {
    return false;
  }

  const isPhoneUA =
    /iPhone|iPod|webOS|BlackBerry|IEMobile|Opera Mini|Mobile/i.test(ua) ||
    (/Android/i.test(ua) && /Mobile/i.test(ua));
  const isSmallPhoneScreen = window.innerWidth <= 640;

  return isPhoneUA || isSmallPhoneScreen;
};

// 路由守卫
router.beforeEach(async (to, _from, next) => {
  // 简单的登录检查
  const isLoggedIn = localStorage.getItem("token");
  const isMobile = isMobileDevice();
  let hasDramaAccess = false;
  let hasOptimizerAccess = false;
  let hasDesignerAccess = false;
  let hasCreativeAccess = false;
  let hasUserTraceAccess = false;

  if (isMobile && to.path === "/login") {
    next("/m/login");
    return;
  }

  if (isMobile && (to.path === "/" || to.path === "/home")) {
    next("/m/home");
    return;
  }

  if (to.meta.requiresAuth !== false && !isLoggedIn) {
    next(isMobile ? "/m/login" : "/login");
    return;
  }

  if (isLoggedIn && (to.path === "/login" || to.path === "/m/login")) {
    next(isMobile ? "/m/home" : "/home");
    return;
  }

  // 已登录：先用本地缓存渲染，再从服务端同步菜单/权限/角色（刷新页面即生效）
  if (isLoggedIn) {
    const { useMenuStore } = await import("@/stores/menu");
    const { useUserStore } = await import("@/stores/user");
    const menuStore = useMenuStore();
    const userStore = useUserStore();

    if (menuStore.menus.length === 0) {
      menuStore.restoreFromStorage();
    }

    await Promise.all([
      menuStore.syncFromServer(),
      userStore.refreshUserInfo(true),
    ]);

    hasDramaAccess = canAccessDramaData(menuStore.menus);
    hasOptimizerAccess = hasMenuPathAccess(menuStore.menus, "/data/optimizer");
    hasDesignerAccess = hasMenuPathAccess(menuStore.menus, "/data/designer");
    hasCreativeAccess = hasMenuPathAccess(menuStore.menus, "/data/creative");
    hasUserTraceAccess = hasMenuPathAccess(menuStore.menus, "/data/user-trace");

    if (
      to.meta.requiresAuth !== false &&
      !to.path.startsWith("/m/") &&
      menuStore.menus.length > 0 &&
      !isPathAllowedAfterMenuSync(menuStore.menus, to.path)
    ) {
      const fallback =
        menuStore.menus[0]?.redirect ||
        getFirstMenuPath(menuStore.menus) ||
        "/home";
      if (fallback && fallback !== to.path) {
        next(fallback);
        return;
      }
    }
  }

  if (isMobile && to.path === "/data/drama") {
    next(hasDramaAccess ? "/m/drama-ranking" : "/m/home");
    return;
  }

  if (to.path === "/m/drama-ranking" && !hasDramaAccess) {
    next("/m/home");
    return;
  }

  if (isMobile && to.path === "/data/optimizer") {
    next(hasOptimizerAccess ? "/m/optimizer-ranking" : "/m/home");
    return;
  }

  if (to.path === "/m/optimizer-ranking" && !hasOptimizerAccess) {
    next("/m/home");
    return;
  }

  if (isMobile && to.path === "/data/designer") {
    next(hasDesignerAccess ? "/m/designer-ranking" : "/m/home");
    return;
  }

  if (to.path === "/m/designer-ranking" && !hasDesignerAccess) {
    next("/m/home");
    return;
  }

  if (isMobile && to.path === "/data/creative") {
    next(hasCreativeAccess ? "/m/creative-ranking" : "/m/home");
    return;
  }

  if (to.path === "/m/creative-ranking" && !hasCreativeAccess) {
    next("/m/home");
    return;
  }

  if (isMobile && to.path === "/data/user-trace") {
    next(hasUserTraceAccess ? "/m/user-trace-ranking" : "/m/home");
    return;
  }

  if (to.path === "/m/user-trace-ranking" && !hasUserTraceAccess) {
    next("/m/home");
    return;
  }

  // 需要管理员/超级管理员才能访问的页面（如回传扣量配置）。
  if (to.meta?.requireAdmin && isLoggedIn) {
    const { useUserStore } = await import("@/stores/user");
    const userStore = useUserStore();
    if (!userStore.isAdmin) {
      next("/home");
      return;
    }
  }

  // 移动端页面禁止缩放
  const vp = document.querySelector('meta[name="viewport"]');
  if (to.path.startsWith("/m/")) {
    vp?.setAttribute(
      "content",
      "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no",
    );
  } else if (_from.path.startsWith("/m/")) {
    vp?.setAttribute("content", "width=device-width, initial-scale=1.0");
  }

  next();
});

// 处理动态入口加载失败的情况（例如开发服务器断开或前端文件更新）
router.onError((error, to) => {
  if (
    error.message.includes("Failed to fetch dynamically imported module") ||
    error.message.includes("Importing a module script failed")
  ) {
    console.error("Dynamic import error, reloading page.", error);
    if (!to?.fullPath) {
      window.location.reload();
    } else {
      window.location.href = to.fullPath;
    }
  }
});

export default router;
